import React, { useEffect, useState } from 'react';
import _ from 'lodash';
import { Table } from 'antd';
import { useTranslation } from 'react-i18next';
import AnalyticsPage from '../../shared-components/analytics/AnalyticsPage';
import QualityNote from '../../shared-components/analytics/QualityNote';
import StatTile from '../../shared-components/analytics/StatTile';
import TrendChart from '../../shared-components/charts/TrendChart';
import Delta from '../../shared-components/charts/Delta';
import { getSalesHierarchy, getSalesTrend } from '../../../analytics/analyticsApi';
import { thousand, toCurrency } from '../../../functions';

const pct = (v) => (v == null ? '—' : `${Number(v).toFixed(1)}%`);

function AnalyticsStores() {
  const { t } = useTranslation();
  const [stores, setStores] = useState(null);
  const [trend, setTrend] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([getSalesHierarchy({ groupBy: 5 }), getSalesTrend({ days: 30 })])
      .then(([s, tr]) => { setStores(s); setTrend(tr); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const rows = stores?.items ?? [];
  const totals = stores?.totals;
  const points = trend?.items ?? [];

  const yoyAvg = points.length
    ? _.sumBy(points, 'yoyChangePct') / points.length
    : null;
  const best = _.maxBy(rows, 'netSalesAmount');

  return (
    <AnalyticsPage
      title={t('analytics_stores_title')}
      subtitle={t('analytics_stores_subtitle')}
    >
      <div className="mb-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatTile label={t('analytics_net_sales')} value={toCurrency(totals?.netSalesAmount)} emphasis />
        <StatTile label={t('analytics_margin_pct')} value={pct(totals?.marginPct)} />
        <StatTile
          label={t('analytics_yoy')}
          value={pct(yoyAvg)}
          hint={t('analytics_yoy_help')}
        />
        <StatTile label={t('analytics_top_store')} value={best?.name ?? '—'} sub={toCurrency(best?.netSalesAmount)} />
      </div>

      <div className="mb-5 rounded-card border border-line bg-surface p-4 shadow-card">
        <div className="mb-1 text-sm font-semibold text-ink">{t('analytics_daily_series')}</div>
        <p className="mb-3 text-xs text-muted">{t('analytics_yoy_help')}</p>
        <TrendChart
          points={points}
          yKey="netSalesAmount"
          compareKey="priorYearGrossSalesAmount"
          seriesLabel={t('analytics_net_sales')}
          compareLabel={t('analytics_prior_year')}
          formatValue={(v) => toCurrency(v)}
        />
      </div>

      <div className="rounded-card border border-line bg-surface p-4 shadow-card">
        <div className="mb-3 text-sm font-semibold text-ink">{t('analytics_store_scorecard')}</div>
        <Table
          size="small"
          bordered={false}
          loading={loading}
          rowKey="key"
          pagination={false}
          scroll={{ x: 'max-content' }}
          dataSource={rows}
          locale={{ emptyText: t('no_data') }}
          columns={[
            {
              title: t('analytics_store'),
              key: 'name',
              render: (r) => <span className="text-sm font-semibold text-ink">{r.businessName ?? r.name}</span>,
            },
            {
              title: t('analytics_net_sales'),
              key: 'net',
              align: 'right',
              render: (r) => <span className="tabular-nums">{toCurrency(r.netSalesAmount)}</span>,
            },
            {
              title: t('analytics_gross_profit'),
              key: 'profit',
              align: 'right',
              render: (r) => <span className="tabular-nums">{toCurrency(r.grossProfitAmount)}</span>,
            },
            {
              title: t('analytics_margin_pct'),
              key: 'margin',
              align: 'right',
              render: (r) => <span className="tabular-nums font-semibold text-ink">{pct(r.marginPct)}</span>,
            },
            {
              title: t('analytics_vs_group'),
              key: 'vs',
              align: 'right',
              render: (r) => <Delta current={r.marginPct} previous={totals?.marginPct} />,
            },
            {
              title: t('analytics_share'),
              key: 'share',
              align: 'right',
              render: (r) => <span className="tabular-nums text-muted">{pct(r.shareOfTotalPct)}</span>,
            },
            {
              title: t('analytics_sales_lines'),
              key: 'lines',
              align: 'right',
              render: (r) => <span className="tabular-nums text-muted">{thousand(r.salesLineCount)}</span>,
            },
          ]}
          summary={() => (
            <Table.Summary.Row className="bg-gold-50 font-semibold">
              <Table.Summary.Cell index={0}>{t('total')}</Table.Summary.Cell>
              <Table.Summary.Cell index={1} align="right">
                <span className="tabular-nums">{toCurrency(totals?.netSalesAmount)}</span>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={2} align="right">
                <span className="tabular-nums">{toCurrency(totals?.grossProfitAmount)}</span>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={3} align="right">
                <span className="tabular-nums">{pct(totals?.marginPct)}</span>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={4} />
              <Table.Summary.Cell index={5} align="right">100.0%</Table.Summary.Cell>
              <Table.Summary.Cell index={6} align="right">
                <span className="tabular-nums">{thousand(totals?.salesLineCount)}</span>
              </Table.Summary.Cell>
            </Table.Summary.Row>
          )}
        />
        <QualityNote quality={stores?.quality} className="mt-4 border-t border-line pt-3" />
      </div>
    </AnalyticsPage>
  );
}

export default AnalyticsStores;
